import { useMemo } from 'react';
import { useAppStore } from '../store';
import type { Transaction } from '../types';

export interface InstallmentSummary {
  groupId: string;
  merchantName: string;
  cardIdentifier: string;
  totalInstallments: number;
  paidInstallments: number;
  remainingInstallments: number;
  installmentAmount: number;
  originalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  transactions: Transaction[];
  startDate: string;
  latestDate: string;
}

export function useInstallmentGroups(): InstallmentSummary[] {
  const transactions = useAppStore((state) => state.transactions);

  return useMemo(() => {
    const groupMap = new Map<string, Transaction[]>();

    transactions.forEach((tx) => {
      if (tx.isInstallment && tx.installmentInfo?.groupId) {
        const existing = groupMap.get(tx.installmentInfo.groupId) || [];
        existing.push(tx);
        groupMap.set(tx.installmentInfo.groupId, existing);
      }
    });

    const summaries: InstallmentSummary[] = [];

    groupMap.forEach((txns, groupId) => {
      if (txns.length === 0) return;

      const sortedTxns = [...txns].sort((a, b) => a.date.localeCompare(b.date));
      const firstTx = sortedTxns[0];
      const latestTx = sortedTxns[sortedTxns.length - 1];

      if (!firstTx.installmentInfo) return;

      const totalInstallments = firstTx.installmentInfo.total;
      const paidInstallments = txns.length;
      const remainingInstallments = totalInstallments - paidInstallments;
      const installmentAmount = Math.abs(firstTx.amount);
      const originalAmount = installmentAmount * totalInstallments;
      const paidAmount = installmentAmount * paidInstallments;
      const remainingAmount = installmentAmount * remainingInstallments;

      summaries.push({
        groupId,
        merchantName: firstTx.description,
        cardIdentifier: firstTx.cardIdentifier,
        totalInstallments,
        paidInstallments,
        remainingInstallments,
        installmentAmount,
        originalAmount,
        paidAmount,
        remainingAmount,
        transactions: sortedTxns,
        startDate: firstTx.date,
        latestDate: latestTx.date,
      });
    });

    return summaries.sort((a, b) => b.latestDate.localeCompare(a.latestDate));
  }, [transactions]);
}

export function useInstallmentGroup(
  groupId: string
): InstallmentSummary | undefined {
  const groups = useInstallmentGroups();
  return useMemo(
    () => groups.find((g) => g.groupId === groupId),
    [groups, groupId]
  );
}

export function useTransactionInstallmentInfo(
  transaction: Transaction
): InstallmentSummary | undefined {
  const groups = useInstallmentGroups();
  return useMemo(() => {
    if (!transaction.isInstallment || !transaction.installmentInfo?.groupId) {
      return undefined;
    }
    return groups.find(
      (g) => g.groupId === transaction.installmentInfo?.groupId
    );
  }, [groups, transaction]);
}

export function calculateInstallmentProgress(
  current: number,
  total: number
): number {
  return Math.round((current / total) * 100);
}

export function formatInstallmentProgress(
  current: number,
  total: number
): string {
  return `${current} de ${total}`;
}
